import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress, Alert, Paper, Divider, TextField, InputAdornment } from '@mui/material';
import { internshipDomain } from '../../util/domain';

const PaymentLinkv1 = () => {
    const { hallticketid } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [student, setStudent] = useState(null);
    const [paymentLink, setPaymentLink] = useState(null);
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const [sendOtpLoading, setSendOtpLoading] = useState(false);
    const [purpose, setPurpose] = useState('');
    const [amount, setAmount] = useState('');
    const [payLoading, setPayLoading] = useState(false);
    const [info, setInfo] = useState('');
    const timerRef = useRef();

    // Fetch student/payment info on load
    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(`${internshipDomain}/api/paymentlinkv1/pay/${hallticketid}`);
                if (response.data.success) {
                    setStudent(response.data.student);
                    setPaymentLink(response.data.paymentLink || null);
                    // If OTP already sent, set timer
                    if (response.data.paymentLink?.otpdate) {
                        setOtpSent(true);
                        const sentTime = new Date(response.data.paymentLink.otpdate).getTime();
                        const now = Date.now();
                        const diff = Math.max(0, 120 - Math.floor((now - sentTime) / 1000));
                        setOtpTimer(diff);
                    } else {
                        setOtpSent(false);
                        setOtpTimer(0);
                    }
                    // If OTP already verified, check if otpdate is older than 24 hours
                    if (response.data.paymentLink?.otpverified === 1) {
                        const otpDate = response.data.paymentLink?.otpdate ? new Date(response.data.paymentLink.otpdate) : null;
                        const now = new Date();
                        if (otpDate && (now - otpDate) > 24 * 60 * 60 * 1000) {
                            // OTP is too old, require re-verification
                            setOtpVerified(false);
                            setOtp('');
                            setOtpSent(false);
                            setOtpTimer(0);
                            // Send new OTP
                            await axios.post(`${internshipDomain}/api/paymentlinkv1/sendotp`, { hallticketid, resend: true });
                        } else {
                            setOtpVerified(true);
                        }
                    }
                } else {
                    setError(response.data.message || 'No details found for this Hall Ticket ID.');
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching details.');
            } finally {
                setLoading(false);
            }
        };
        if (hallticketid) fetchDetails();
        // Cleanup timer on unmount
        return () => clearInterval(timerRef.current);
    }, [hallticketid]);

    // OTP resend timer
    useEffect(() => {
        if (otpSent && otpTimer > 0) {
            timerRef.current = setInterval(() => {
                setOtpTimer(t => {
                    if (t <= 1) {
                        clearInterval(timerRef.current);
                        return 0;
                    }
                    return t - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [otpSent, otpTimer]);

    // Send OTP handler
    const handleSendOtp = async (resend = false) => {
        setSendOtpLoading(true);
        setOtpError('');
        try {
            const body = { hallticketid };
            if (resend) body.resend = true;
            const response = await axios.post(`${internshipDomain}/api/paymentlinkv1/sendotp`, body);
            if (response.data.success && response.data.paymentLink) {
                setOtpSent(true);
                setPaymentLink(response.data.paymentLink);
                setOtp('');
                // Reset timer
                const sentTime = new Date(response.data.paymentLink.otpdate).getTime();
                const now = Date.now();
                const diff = Math.max(0, 120 - Math.floor((now - sentTime) / 1000));
                setOtpTimer(diff);
            } else {
                setOtpError(response.data.message || 'Failed to send OTP.');
            }
        } catch (err) {
            setOtpError(err.response?.data?.message || 'Error sending OTP.');
        } finally {
            setSendOtpLoading(false);
        }
    };

    // OTP verification handler
    const handleVerifyOtp = async () => {
        setOtpLoading(true);
        setOtpError('');
        try {
            const response = await axios.post(`${internshipDomain}/api/paymentlinkv1/verifyotp`, {
                transaction_id: paymentLink.transaction_id,
                otp
            });
            if (response.data.success) {
                setOtpVerified(true);
            } else {
                setOtpError(response.data.message || 'Invalid OTP.');
            }
        } catch (err) {
            setOtpError(err.response?.data?.message || 'Error verifying OTP.');
        } finally {
            setOtpLoading(false);
        }
    };

    // Payment handler
    const handlePay = async () => {
        // Validate amount and purpose
        if (!amount || isNaN(amount) || Number(amount) <= 10) {
            setInfo('Please enter a valid amount greater than 10.');
            return;
        }
        if (!purpose || purpose.trim().length === 0) {
            setInfo('Please enter a purpose/description for the payment.');
            return;
        }
        setPayLoading(true);
        setError('');
        setInfo('');
        try {
            const response = await axios.post(`${internshipDomain}/api/paymentlinkv1/pay`, {
                hall_ticket_id: hallticketid,
                amount,
                purpose,
                transaction_id: paymentLink?.transaction_id,
                newPayment: (paymentLink?.payment_status === 'SUCCESS' || paymentLink?.payment_status === 'FAILED') ? true : false
            });
            if (response.data.success && response.data.data?.payment_url) {
                window.location.href = response.data.data.payment_url;
            } else {
                setError(response.data.message || 'Failed to initiate payment.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error initiating payment.');
        } finally {
            setPayLoading(false);
        }
    };

    // UI rendering
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" bgcolor="#f8f9fa">
            <Paper elevation={3} sx={{ p: 4, minWidth: 550, maxWidth: 600 }}>
                <center><img src={`${internshipDomain}/vector_logo.jpg`} alt="Vector India" style={{ width: '200px', height: 'auto' }} /></center>
                <Typography variant="h5" fontWeight={700} mb={2} align="center">
                    Fee Payment
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
                        {student && !otpSent ? (
                            <>
                                <Typography variant="subtitle1" gutterBottom>
                                    <b>Name:</b> {student.name || '-'}
                                </Typography>
                                <Typography variant="subtitle1" gutterBottom>
                                    <b>Mobile:</b> {student.mobile || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    If the above information is incorrect or needs to be updated, please contact the front office at <b><a href="tel:9866666699">98666 66699</a></b> for assistance.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleSendOtp(false)}
                                    disabled={sendOtpLoading}
                                    sx={{ mt: 2 }}
                                >
                                    {sendOtpLoading ? <CircularProgress size={24} /> : 'Send OTP'}
                                </Button>
                                {otpError && <Alert severity="error" sx={{ mt: 2 }}>{otpError}</Alert>}
                            </>
                        ) : student && otpSent && !otpVerified ? (
                            <>
                                <Typography variant="subtitle1" gutterBottom>
                                    <b>Name:</b> {student.name || '-'}
                                </Typography>
                                <Typography variant="subtitle1" gutterBottom>
                                    <b>Mobile:</b> {student.mobile || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Please enter the OTP sent to your registered mobile number.
                                </Typography>
                                <TextField
                                    label="Enter OTP"
                                    value={otp}
                                    onChange={e => setOtp(e.target.value)}
                                    size="small"
                                    sx={{ width: 180, mt: 1 }}
                                    inputProps={{ maxLength: 6 }}
                                    error={!!otpError}
                                    helperText={otpError}
                                />
                                <Box display="flex" alignItems="center" gap={2} mt={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleVerifyOtp}
                                        disabled={otpLoading || !otp}
                                    >
                                        {otpLoading ? <CircularProgress size={24} /> : 'Verify OTP'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => handleSendOtp(true)}
                                        disabled={otpTimer > 0 || sendOtpLoading}
                                    >
                                        {otpTimer > 0 ? `Resend OTP (${otpTimer}s)` : 'Resend OTP'}
                                    </Button>
                                </Box>
                            </>
                        ) : student && otpVerified ? (
                            <>
                                {/* Show info message above the form if present */}
                                {(paymentLink?.payment_status === 'SUCCESS' || paymentLink?.payment_status === 'FAILED' || paymentLink?.payment_status === 'PENDING') && paymentLink?.otpverified === 1 && (
                                    <Box mb={3} p={2} sx={{ background: '#f0fff4', border: '1px solid #eb7a24', borderRadius: 2 }}>
                                        <Typography variant="subtitle1" gutterBottom><b>Payment Details</b></Typography>
                                        <Typography variant="body2"><b>Name:</b> {student.name || '-'}</Typography>
                                        <Typography variant="body2"><b>Mobile:</b> {student.mobile || '-'}</Typography>
                                        <Typography variant="body2"><b>Amount:</b> ₹{paymentLink.amount || '-'}</Typography>
                                        <Typography variant="body2"><b>Transaction ID:</b> {paymentLink.transaction_id || '-'}</Typography>
                                        <Typography variant="body2"><b>Status:</b> {paymentLink.payment_status || '-'}</Typography>
                                        <Typography variant="body2"><b>Purpose:</b> {paymentLink.purpose || '-'}</Typography>
                                    </Box>
                                )}
                                <Box mt={2} mb={1}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <b>Name:</b> {student.name || '-'}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <b>Mobile:</b> {student.mobile || '-'}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <b>Amount:</b> <TextField
                                            type="number"
                                            value={amount}
                                            onChange={e => setAmount(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 180, height: 40, ml: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 } }}
                                            inputProps={{ min: 0, style: { padding: '10px 14px', fontSize: 18 } }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                            }}
                                        />
                                    </Typography>
                                </Box>
                                <Box mt={2} mb={1}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <b>Purpose / Description / Notes:</b>
                                    </Typography>
                                    <TextField
                                        value={purpose}
                                        onChange={e => setPurpose(e.target.value)}
                                        placeholder="Enter purpose, description, or notes"
                                        multiline
                                        minRows={2}
                                        maxRows={4}
                                        fullWidth
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handlePay}
                                    disabled={payLoading}
                                    fullWidth
                                >
                                    {payLoading ? <CircularProgress size={24} /> : 'Pay Now'}
                                </Button>
                            </>
                        ) : null}
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default PaymentLinkv1; 